<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-cash-coin me-2"></i>Transactions Management
      </h4>
      <div class="d-flex">
        <button class="btn btn-outline-primary me-2" @click="$router.push('/pos')">
          <i class="bi bi-cart me-1"></i> New Sale
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading transactions...</p>
      </div>

      <!-- Transactions Content -->
      <div v-if="!loading" class="transactions-content fade-in">
        <!-- Transactions Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ transactions.total }}</span>
            <span class="text-muted">Transactions</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search transactions" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Transactions Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>SALE</th>
                  <th>AMOUNT</th>
                  <th>METHOD</th>
                  <th>REFERENCE</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(transaction, index) in transactions.data" :key="transaction.id" class="transaction-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ transaction.id.split("-").pop() }}</td>
                  <td>
                    <router-link
                      v-if="transaction.sale_id"
                      :to="'/sale/' + transaction.sale_id"
                      class="text-decoration-none fw-semibold"
                    >
                      <i class="bi bi-receipt me-1 text-primary"></i>
                      <span>{{ transaction.sale?.reference || transaction.sale_id.split('-').pop() }}</span>
                    </router-link>
                    <span v-else class="text-muted small">-</span>
                  </td>
                  <td class="fw-bold">{{ formatNumber(transaction.amount) }}</td>
                  <td>
                    <Badge pill :type="getMethodBadgeType(transaction.method)">
                      {{ transaction.method.toUpperCase() }}
                    </Badge>
                  </td>
                  <td>{{ transaction.reference === null ? '-' : transaction.reference }}</td>
                  <td>{{ formatDate(transaction.created_at) }}</td>
                  <td class="text-end">
                    <router-link
                      v-if="transaction.sale_id"
                      :to="'/sale/' + transaction.sale_id"
                      class="btn btn-sm btn-outline-primary"
                      title="View Sale"
                    >
                      <i class="bi bi-eye me-1"></i> View Sale
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import transactionService from "@/api/transactionService";
import formatDate from "../../utils/formatDate";
import formatNumber from "@/utils/formatNumber";
import {decrypt} from "@/utils/crypto";
import router from "@/router";
import Badge from "@/components/badge/Badge";

export default {
  name: "Transactions",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    return {
      profile: {},
      loading: true,
      transactions: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
    }
  },

  methods: {
    formatDate,
    formatNumber,
    getTransactions: async function () {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
        store_id: this.$route?.query?.store_id || undefined,
      }
      this.transactions = await transactionService.getTransactions(params)
      this.loading = false
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getTransactions()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getTransactions()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    getMethodBadgeType(method) {
      switch (method.toLowerCase()) {
        case 'cash':
          return 'success';
        case 'mpesa':
          return 'primary';
        case 'card':
          return 'info';
        case 'bank':
          return 'warning';
        default:
          return 'secondary';
      }
    },
  },

  computed: {
    totalPages(){
      return this.transactions?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getTransactions()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getTransactions()
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getTransactions()
  },
}
</script>

<style scoped>
/* Transactions page styles */
.transactions-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.transaction-row {
  transition: all 0.2s ease;
}

.transaction-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
